import React from 'react';
import { RegionIdEnum } from '@search/filter-enums/enums/Region';
import type { BreadcrumbSection } from '@search/vtbeco-ui/src/components/Breadcrumb';
import { GeoUserRegion } from '@search/geo/src/GeoUserRegion';

import type { RegionSelector2Item } from './RegionSelector2Item';

export function useRegionSelector2Modal(
    {
        selectedIdInitial,
        items,
        hasEmptySelect,
        onSelectId
    }: {
        onSelectId?(id?: RegionSelector2Item['id']): void;
        items: ReadonlyMap<RegionSelector2Item['id'], RegionSelector2Item>;
        selectedIdInitial?: RegionSelector2Item['id'];
        hasEmptySelect?: boolean;
    }
) {
    const [ userRegionId1, userRegionId2 ] = new GeoUserRegion().values();

    const [ selectedId, setSelectedId ] = React.useState(selectedIdInitial);

    // eslint-disable-next-line require-await
    const getVariants = React.useCallback(async (needleRaw: string) => {
        const limit = 12;
        const finds = [] as RegionSelector2Item[];
        const needle = needleRaw.toLowerCase().replace('ё', 'е');

        for (const region of items.values()) {
            const fullName = region.displayName?.toLowerCase().replace('ё', 'е');
            const index = needle && fullName?.indexOf(needle);

            if (index !== undefined && index === 0) finds.push(region);
            if (finds.length >= limit) break;
        }

        return finds;
    }, [ items ]);

    const hasChild = React.useMemo(() => {
        const childsMap = new Map<RegionSelector2Item['id'], true>();

        for (const region of items.values()) {
            if (region.parentId) childsMap.set(region.parentId, true);
        }

        return (id: RegionSelector2Item['id']) => childsMap.get(id);
    }, [ items ]);

    const actual = React.useMemo(
        () => {
            const list = Array.from(items.values());
            const primary: typeof list = [];
            const secondary: typeof list = [];
            const secondaryFallback: typeof list = [];

            for (const item of list) {
                if (
                    item.id === RegionIdEnum.MSK ||
                    item.id === RegionIdEnum.MSK_AND_MSK_OBL ||
                    item.id === RegionIdEnum.SPB ||
                    item.id === RegionIdEnum.SPB_AND_LEN_OBL
                ) {
                    primary.push(item);
                }

                if (
                    selectedId === undefined &&
                    item.id === userRegionId1
                ) {
                    primary.push(item);
                    secondary.push(item);
                    continue;
                }

                if (item.parentId === selectedId) secondary.push(item);
                else secondaryFallback.push(item);
            }

            return {
                primary,
                secondary: secondary.length ? secondary : secondaryFallback
            };
        },
        [ items, selectedId, userRegionId1, userRegionId2 ]
    );

    const onSelectIdRef = React.useRef(onSelectId);

    onSelectIdRef.current = onSelectId;

    const onSubmit = React.useCallback((id = selectedId) => {
        if (id || hasEmptySelect) onSelectIdRef.current?.(id);
    }, [ selectedId ]);

    const breadcrumbs = React.useMemo(() => {
        const sections: BreadcrumbSection[] = [];
        let current = selectedId ? items.get(selectedId) : undefined;

        while (current) {
            sections.push({
                href: '#' + current.id,
                key: String(current.id),
                content: current.displayName ?? current.id
            });
            current = current.parentId ? items.get(current.parentId) : undefined;
        }

        sections.push({
            href: '#rus',
            key: undefined,
            content: 'Россия'
        });
        const result = sections.reverse();

        return result.length > 1 ? result.slice(0, -1) : result;
    }, [ items, selectedId ]);

    const current = selectedId ? items.get(selectedId) : undefined;

    const setSelectedIdExt = React.useCallback((id?: RegionSelector2Item['id']) => {
        if (id && ! hasChild(id)) return onSubmit(id);

        setSelectedId(id);
    }, [ hasChild, onSubmit ]);

    return {
        current,
        breadcrumbs,
        setSelectedId: setSelectedIdExt,
        onSubmit,
        getVariants,
        actual
    };
}
